import { useEffect, useRef, useState } from 'react';
import { MODAL_TYPES } from '@components/modals/constants';
import { useBrandLanguage } from '@context/brand-language/context';
import { useGetOnboardingStatusQuery, useSubjectLanguage } from '@features/user';
import { useUserMetrics } from '@features/user/store/hooks';
import { useEventTracking } from '@services/statsig';
import { useModal } from '@store/hooks';
import type { OnboardingStatus, Task } from '@ts/types';
import { trackEvent } from '@utils/analytics_utils';

import type { ModalState } from '../OnboardingTasksCard.types';

interface UseOnboardingTasksProps {
  providedStatus?: OnboardingStatus;
}

export const useOnboardingTasks = ({ providedStatus }: UseOnboardingTasksProps = {}) => {
  // API & Context hooks
  const userMetrics = useUserMetrics();
  const subjectLanguage = useSubjectLanguage();
  const { brandLanguage } = useBrandLanguage();
  const { dashboard } = useEventTracking();
  const { open: openModal } = useModal();
  
  // RTK Query hooks
  const { data: onboardingData, isLoading: isLoadingOnboardingStatus } =
    useGetOnboardingStatusQuery(undefined, {
      skip: !!providedStatus,
    });

  // State management
  const [modalState, setModalState] = useState<ModalState>({
    isOpen: false,
    task: null,
  });
  const [showShadow, setShowShadow] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Computed values
  const onboardingStatus = providedStatus || onboardingData?.onboarding_status;
  const isLoading = !providedStatus && isLoadingOnboardingStatus;
  
  const tasks = onboardingStatus?.tasks
    ? [...onboardingStatus.tasks].sort((a, b) => a.seq - b.seq)
    : [];
  
  const completedTasks = tasks.filter(task => task.is_completed).length;
  const progressPercentage = tasks.length > 0 ? (completedTasks / tasks.length) * 100 : 0;
  
  const showOnboarding = onboardingStatus?.show_onboarding_tasks && 
                         onboardingStatus?.tasks && 
                         onboardingStatus.tasks.length > 0;

  // Scroll handling
  useEffect(() => {
    const handleScroll = () => {
      if (scrollContainerRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
        setShowShadow(scrollTop < scrollHeight - clientHeight - 10);
      }
    };

    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      handleScroll(); // Check initial scroll state

      return () => {
        scrollContainer.removeEventListener('scroll', handleScroll);
      };
    }
  }, []);

  // Event handlers

  const handleOpenModal = (task: Task) => {
    // Track with old system for backward compatibility
    trackEvent({
      event: `onboarding_task_explanation_modal_open_${task.task_id}`,
    });

    // Track with new Statsig system
    const remainingTasks = tasks.filter(t => !t.is_completed).length;
    dashboard.onboardingTaskClicked({
      task_type: task.task_id,
      tasks_remaining: remainingTasks,
      onboarding_step: task.title,
    });

    // Open the appropriate modal
    if (task.task_id === 'consent_information') {
      // Modal is self-contained and handles everything internally
      openModal(MODAL_TYPES.CONSENT_INFORMATION, {});
    } else {
      // Keep the existing modal state for other tasks
      setModalState({
        isOpen: true,
        task,
      });
    }
  };

  const handleCloseModal = () => {
    setModalState({
      isOpen: false,
      task: null,
    });
  };

  const trackVideoPlay = (taskId: string) => {
    trackEvent({
      event: `onboarding_task_video_play_${taskId}`,
    });
  };

  const trackTemplateDownload = () => {
    trackEvent({
      event: 'consent_template_download_clicked',
    });
  };

  const trackLearnMoreClick = () => {
    trackEvent({
      event: 'learn_more_about_consent_clicked',
    });
  };

  return {
    // Data
    tasks,
    onboardingStatus,
    isLoading,
    showOnboarding,
    userMetrics,
    subjectLanguage: subjectLanguage || 'patient',
    brandLanguage,
    
    // Progress
    completedTasks,
    totalTasks: tasks.length,
    progressPercentage,
    
    // Modal state
    modalState,
    
    // UI state
    showShadow,
    scrollContainerRef,
    
    // Handlers
    handleOpenModal,
    handleCloseModal,
    trackVideoPlay,
    trackTemplateDownload,
    trackLearnMoreClick,
  };
};